import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { stores } from './stores';
import logger from './logging/logger.js';
import { getDefaultRedirectPathForRoles } from '@/router/defaultRoleDestination';
import { broadcastSessionUpdated, emitAuthSessionUpdated } from '@/util/authSession';
import { emergencyContactErrors, hasProfileValue, mailingAddressErrors, parentalContactErrors } from '@/util/profileRequirements';

type DynamicRecord = Record<string, unknown>;
type DateInput = string | number | Date | null | undefined;
type MoneyValue = string | number | null | undefined;
type NormalizeValue = (value: unknown, key?: string) => unknown;

interface MoneyFormatOptions {
	currency?: string | undefined;
	locale?: string | undefined;
	minimumFractionDigits?: number | undefined;
	maximumFractionDigits?: number | undefined;
}

interface UserRecord extends DynamicRecord {
	userNumber?: number | null | undefined;
	firstName?: string | undefined;
	commonFirstName?: string | null | undefined;
	middleName?: string | null | undefined;
	lastName?: string | undefined;
	birthdate?: string | null | undefined;
	userGender?: string | null | undefined;
	emergencyFirstName?: string | null | undefined;
	emergencyLastName?: string | null | undefined;
	emergencyRelation?: string | null | undefined;
	emergencyPhone?: string | null | undefined;
	parentalFirstName?: string | null | undefined;
	parentalLastName?: string | null | undefined;
	parentalPhone?: string | null | undefined;
	parentalEmail?: string | null | undefined;
	gender?: string | null | undefined;
	primaryEmail?: string | null | undefined;
	secondaryEmail?: string | null | undefined;
	primaryPhone?: string | null | undefined;
	secondaryPhone?: string | null | undefined;
	addressLine1?: string | null | undefined;
	addressLine2?: string | null | undefined;
	city?: string | null | undefined;
	state?: string | null | undefined;
	postalCode5?: string | null | undefined;
	postalCode4?: string | null | undefined;
	country?: string | null | undefined;
	groups?: DynamicRecord[] | undefined;
	photo?: unknown;
}

interface UserSummary extends DynamicRecord {
	userNumber?: number | null | undefined;
	firstName?: string | undefined;
	middleName?: string | null | undefined;
	lastName?: string | undefined;
}

interface LocationRecord extends DynamicRecord {
	name?: string | null | undefined;
	addressLine1?: string | null | undefined;
	city?: string | null | undefined;
	state?: string | null | undefined;
	postalCode5?: string | null | undefined;
}

interface MatchRecord extends DynamicRecord {
	groupMatchNumber?: number | string | undefined;
	matchNumber?: number | undefined;
	matchDate?: string | undefined;
	matchTime?: string | undefined;
	dateTime?: string | undefined;
	field?: string | null | undefined;
	location?: LocationRecord | null | undefined;
	fieldEntity?: DynamicRecord | null | undefined;
	center?: UserSummary | null | undefined;
	ar1?: UserSummary | null | undefined;
	ar2?: UserSummary | null | undefined;
	fourth?: UserSummary | null | undefined;
	mentor?: UserSummary | null | undefined;
	crStatus?: string | null | undefined;
	ar1Status?: string | null | undefined;
	ar2Status?: string | null | undefined;
	fourthStatus?: string | null | undefined;
	mentorStatus?: string | null | undefined;
	status?: string | null | undefined;
	visibility?: string | null | undefined;
	matchRank?: number | null | undefined;
	client?: DynamicRecord | null | undefined;
}

interface RankingRecord extends DynamicRecord {
	client?: DynamicRecord | null | undefined;
	user?: UserSummary | null | undefined;
	crRank?: number | null | undefined;
	arRank?: number | null | undefined;
}

interface ClientRecord extends DynamicRecord {
	clientNumber?: number | undefined;
}

interface GroupRecord extends DynamicRecord {
	groupNumber: number;
}

interface RefLike<T> {
	value: T;
}

interface RoleGroup {
	id: string;
	label: string;
	roles: string[];
}

interface GroupRolesUtil {
	SYSTEM_ROLES: Set<string>;
	HIDDEN_BY_DEFAULT_ROLES: Set<string>;
	ENABLED_ROLE_GROUP_IDS: Set<string>;
	roleLabel(role: unknown): string;
	normalizeRoleNames(roles: unknown): string[];
	getRoleGroups(options?: { includeHidden?: boolean }): RoleGroup[];
	getAssignableRoleSet(): Set<string>;
}

interface EgsUtil {
	formatDate(dateString: DateInput, format?: string): string;
	formatTime(timeString: string | null | undefined, timeFormat?: string): string;
	formatAge(dateString: string | number | null | undefined): number | null;
	formatPhone(phoneString: string | number): string | null;
	formatMoney(value: MoneyValue, options?: MoneyFormatOptions): string | number;
	formatSaveDate(value: unknown): string;
	normalizedTimeInput(value: unknown): string;
	formatSaveTime(timeString: unknown): string;
	assignmentChange(position: string, action: string, matchData: DynamicRecord, user?: UserSummary | null): void;
	uploadHeaders(): Record<string, string>;
	exportHeaders(): string[];
	exportMatches(matches: MatchRecord[]): void;
	convertUploadData(header: string): string;
	formatUser(user: UserRecord): {
		userNumber?: number | undefined;
		fullName: string;
		firstLast: string;
	};
	formatUserWithNumber(user: UserRecord | null | undefined): string;
	initializeUser(userInformation: string): Promise<string>;
	fetchAssignorsClients(): Promise<ClientRecord[]>;
	fetchMatchesForSelectedGroups(selectedGroups: RefLike<GroupRecord[]>, user: UserRecord | null | undefined): Promise<void>;
	officialIsRanked(position: string, match: MatchRecord | null | undefined, currentUser: UserRecord | null | undefined, rankings: RankingRecord[] | null | undefined): boolean;
	isLoggedInBase(): boolean;
	getUserRoles(): string[];
	hasAnyRole(requiredRoles: string[]): boolean;
	canImpersonate(): boolean;
	formatLocation(obj: LocationRecord | null | undefined): string;
	formatLocationDisplay(match: MatchRecord | null | undefined): string;
	findNextAvailableAssignorSlot(client: ClientRecord): string | null;
	groupRoles: GroupRolesUtil;
	cloneValue<T>(value: T): T;
	isPlainObject(value: unknown): value is DynamicRecord;
	buildMergePatch(original: DynamicRecord | null | undefined, current: DynamicRecord | null | undefined, normalizeValue?: NormalizeValue): DynamicRecord;
	normalizeValue(value: unknown): unknown;
}

interface BasicInfoRecord extends DynamicRecord {
	userNumber?: number | undefined;
	firstName?: string | undefined;
	commonFirstName?: string | null | undefined;
	middleName?: string | null | undefined;
	lastName?: string | undefined;
	birthdate?: string | null | undefined;
	userGender?: string | null | undefined;
	parentalFirstName?: string | null | undefined;
	parentalLastName?: string | null | undefined;
	parentalPhone?: string | null | undefined;
	parentalEmail?: string | null | undefined;
}

interface ContactInfoRecord extends DynamicRecord {
	userNumber?: number | undefined;
	primaryEmail?: string | null | undefined;
	secondaryEmail?: string | null | undefined;
	primaryPhone?: string | null | undefined;
	secondaryPhone?: string | null | undefined;
	emergencyFirstName?: string | null | undefined;
	emergencyLastName?: string | null | undefined;
	emergencyRelation?: string | null | undefined;
	emergencyPhone?: string | null | undefined;
}

interface MailingInfoRecord extends DynamicRecord {
	userNumber?: number | undefined;
	addressLine1?: string | null | undefined;
	addressLine2?: string | null | undefined;
	city?: string | null | undefined;
	state?: string | null | undefined;
	postalCode5?: string | null | undefined;
	postalCode4?: string | null | undefined;
	country?: string | null | undefined;
}

interface FinancialContext {
	userRoles?: string[] | undefined;
	/** From GET /api/v2/finance/me/summary: payouts are set up, or no group pays this person through Stripe. */
	payoutSetupComplete?: boolean | undefined;
}

interface UserInformationUtil {
	fullName(): string;
	name(): string;
	basicInfo(): BasicInfoRecord;
	setBasicInfo(data: BasicInfoRecord | null | undefined): void;
	contactInfo(): ContactInfoRecord;
	setContactInfo(data: ContactInfoRecord | null | undefined): void;
	mailingInfo(): MailingInfoRecord;
	setMailingInfo(data: MailingInfoRecord | null | undefined): void;
	userDTO(): UserSummary;
	convertToDTO(user: UserRecord): UserSummary;
	userGroups(): DynamicRecord[] | undefined;
	setUserGroups(groups: DynamicRecord[]): void;
	fullUser(): DynamicRecord;
	readSessionUserInfo(): UserRecord | null;
	syncSessionUserInfo(nextUser: UserRecord | null | undefined): void;
	isBasicProfileComplete(data: DynamicRecord): boolean;
	isProfileComplete(data: DynamicRecord): boolean;
	financialProfileRoleNames(): string[];
	isOverallProfileComplete(userData: DynamicRecord, financialContext?: FinancialContext | null): boolean;
}

interface MatchDtoUtil {
	fullMatch(match: MatchRecord): MatchRecord;
}

interface SelectOption {
	label: string;
	value: string;
}

interface EgsEnums {
	assignmentMethods: string[];
	positionStatus: string[];
	assignFilters: string[];
	matchStatus: string[];
	matchVisibility: string[];
	matchGenders: string[];
	gender: string[];
	matchReportStatus: string[];
	ledgerEntryType: string[];
	creditTypes: string[];
	debitTypes: string[];
	warningTypes: string[];
	manualEntryTypes: string[];
	paymentMethods: string[];
	durationTypes: string[];
	fieldTypeOptions: string[];
	waypointTypes: string[];
	groupPlan: string[];
	groupJoinStatus: string[];
	groupStatus: string[];
	groupType: string[];
	groupSport: string[];
	groupTravelEnforcement: string[];
	reportType: string[];
	yesNoOptions: string[];
	datePresets: SelectOption[];
	availabilityFilters: SelectOption[];
	requestFilters: string[];
	SignupStatus: Record<string, string>;
	emailStatusOptions: string[];
	emailTypeOptions: string[];
	groupPlanEntitlements: Record<string, object>;
	LICENSE_LABELS: Record<string, Record<number, string>>;
}

interface ValidationResult {
	isValid: boolean;
	errors: Record<string, string>;
}

interface ValidationUtil {
	isRequired(value: unknown): boolean;
	isEmail(value: unknown): boolean;
	isPhone(value: unknown): boolean;
	isPositiveNumber(value: unknown): boolean;
	isNonNegativeNumber(value: unknown): boolean;
	isDate(value: unknown): boolean;
	isPayPeriod(value: unknown): boolean;
	validateFields(data: DynamicRecord | null | undefined, requiredFields: string[]): ValidationResult;
	invalidClass(condition: boolean): string;
	isUsAbaRoutingNumber(value: unknown): boolean;
	isUsBankAccountNumber(value: unknown): boolean;
}

interface Egs {
	util: EgsUtil;
	UserInformation: UserInformationUtil;
	MatchDTO: MatchDtoUtil;
	Enums: EgsEnums;
	Validation: ValidationUtil;
}

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const toText = (value: unknown): string => {
	if (value === null || value === undefined) {
		return '';
	}
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
		return String(value);
	}
	if (typeof value === 'symbol') {
		return value.description ?? '';
	}
	if (value instanceof Date) {
		return value.toISOString();
	}

	try {
		return JSON.stringify(value) || '';
	} catch {
		return '';
	}
};

const escapeCsvField = (value: unknown): string => `"${toText(value).replace(/"/g, '""')}"`;

const getUser = (): UserRecord | null => {
	const userData = sessionStorage.getItem('user_info');
	return userData ? (JSON.parse(userData) as UserRecord) : null;
};

const EGS: Egs = {
	util: {
		formatDate(dateString, format) {
			if (!dateString) {
				return '';
			}

			const date = dayjs(dateString);

			switch (format) {
				case 'us-long':
					return date.format('MM/DD/YYYY');
				case 'us-short':
					return date.format('MM/DD/YY');
				case 'eu-long':
					return date.format('DD/MM/YYYY');
				case 'eu-short':
					return date.format('DD/MM/YY');
				default:
					return date.format('MM/DD/YYYY');
			}
		},
		formatTime(timeString, timeFormat) {
			if (!timeString) {
				return '';
			}

			const time = dayjs(timeString.trim(), ['H:mm', 'HH:mm', 'H:mm:ss', 'HH:mm:ss', 'h:mm A', 'hh:mm A', 'h:mm:ss A', 'hh:mm:ss A', 'h:mm a', 'hh:mm a', 'h:mm:ss a', 'hh:mm:ss a'], true);

			if (!time.isValid()) {
				logger.error('Invalid time format', { timeString });
				return 'Invalid Time';
			}

			return timeFormat === '24 HOUR' ? time.format('HH:mm') : time.format('h:mm A');
		},
		formatAge(dateString) {
			if (dateString === null || dateString === undefined) {
				return null;
			}
			const birthDate = new Date(dateString);
			const currentDate = new Date();

			let age = currentDate.getFullYear() - birthDate.getFullYear();
			const m = currentDate.getMonth() - birthDate.getMonth();

			if (m < 0 || (m === 0 && currentDate.getDate() < birthDate.getDate())) {
				age--;
			}

			return age;
		},
		formatPhone(phoneString) {
			const cleaned = String(phoneString).replace(/\D/g, '');
			const match = /^(1|)?(\d{3})(\d{3})(\d{4})$/.exec(cleaned);

			if (match) {
				const intlCode = match[1] ? '+1 ' : '';
				return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
			}
			return null;
		},
		formatMoney(value, { currency = 'USD', locale = 'en-US', minimumFractionDigits, maximumFractionDigits } = {}) {
			if (value === null || value === undefined || value === '') {
				return '$0.00';
			}
			let amount = value;

			if (typeof amount === 'string') {
				const parsed = Number(amount.replace(/,/g, ''));

				if (!Number.isNaN(parsed)) {
					amount = parsed;
				}
			}

			if (typeof amount !== 'number' || !Number.isFinite(amount)) {
				return value || '$0.00';
			}

			const options: Intl.NumberFormatOptions = {
				style: 'currency',
				currency
			};

			if (minimumFractionDigits !== undefined) {
				options.minimumFractionDigits = minimumFractionDigits;
			}
			if (maximumFractionDigits !== undefined) {
				options.maximumFractionDigits = maximumFractionDigits;
			}

			return new Intl.NumberFormat(locale, options).format(amount);
		},
		formatSaveDate(value) {
			if (!value) {
				return '';
			}

			if (value instanceof Date) {
				if (Number.isNaN(value.getTime())) {
					throw new Error('invalid date format');
				}
				return dayjs(value).format('YYYY-MM-DD');
			}

			const raw = toText(value).trim();

			const FORMATS_TRY_ORDER = [
				'YYYY-MM-DD',
				'YYYY/M/D',
				'YYYY/MM/DD',
				'YYYY.M.D',
				'YYYY.MM.DD',
				'MMMM D, YYYY',
				'MMM D, YYYY',
				'D MMM YYYY',
				'DD MMM YYYY',
				'MMM D YYYY',
				'MMMM D YYYY',
				'D-MMM-YYYY',
				'DD-NN-YYYY',
				'D MMM, YYYY',
				'DD MMM, YYYY',
				'M/D/YYYY',
				'MM/D/YYYY',
				'M/DD/YYYY',
				'MM/DD/YYYY',
				'D/M/YYYY',
				'DD/M/YYYY',
				'D/MM/YYYY',
				'DD/MM/YYYY',
				'M-D-YYYY',
				'MM-D-YYYY',
				'M-DD-YYYY',
				'MM-DD-YYYY',
				'D-M-YYYY',
				'DD-M-YYYY',
				'D-MM-YYYY',
				'DD-MM-YYYY',
				'M.D.YYYY',
				'MM.D.YYYY',
				'M.DD.YYYY',
				'MM.DD.YYYY',
				'D.M.YYYY',
				'DD.M.YYYY',
				'D.MM.YYYY',
				'DD.MM.YYYY',
				'M/D/YY',
				'MM/D/YY',
				'M/DD/YY',
				'MM/DD/YY',
				'D/M/YY',
				'DD/M/YY',
				'D/MM/YY',
				'DD/MM/YY',
				'YY-M-D',
				'YY-MM-DD',
				'YY/M/D',
				'YY/MM/DD'
			];

			const normalized = raw.replace(/\s{2,}/g, ' ').replace(/-/g, '-');
			const parsed = dayjs(normalized, FORMATS_TRY_ORDER, true);
			if (!parsed.isValid()) {
				throw new Error('invalid date format');
			}

			return parsed.format('YYYY-MM-DD');
		},
		normalizedTimeInput(s) {
			let t = toText(s).trim();

			t = t.replace(/\./g, '').replace(/\s+/g, '').toUpperCase();
			if (/^\d{3,4}$/.test(t) && !/[AP]M$/.test(t)) {
				const h = t.length === 3 ? t.slice(0, 1) : t.slice(0, 2);
				const m = t.slice(-2);
				t = `${Number(h).toString()}:${m}`;
			}
			t = t.replace(/^(\d{1,2})([AP]M)$/, '$1 $2');
			t = t.replace(/^(\d{1,2})\s([AP]M)$/, '$1:00 $2');

			if (/^\d{1,2}$/.test(t)) {
				t = `${t}:00`;
			}

			return t;
		},
		formatSaveTime(timeString) {
			if (!timeString) {
				return '';
			}

			const t = this.normalizedTimeInput(timeString);

			const TIME_FORMATS = [
				'HH:mm:ss',
				'H:mm:ss',
				'HH:mm',
				'H:m',
				'HH.mm',
				'H.mm',
				'h:mm:ss A',
				'h:mm:ss a',
				'h:mm A',
				'h:mm a',
				'h.mm A',
				'h.mm a',
				'h A',
				'h a',
				'hh:mm:ss A',
				'hh:mm:ss a',
				'hh:mm A',
				'hh:mm a',
				'hh A',
				'hh a',
				'h:mma',
				'h:mmA',
				'hmm A',
				'hmmA',
				'hmm',
				'HHmm',
				'Hmm',
				'h:mm:ss a.m.',
				'h:mm:ss p.m.',
				'h:mm a.m.',
				'h:mm p.m.',
				'h a.m.',
				'h p.m.'
			];

			const parsed = dayjs(t, TIME_FORMATS, true);

			if (!parsed.isValid()) {
				throw new Error('invalid time format');
			}

			return parsed.format('HH:mm:ss');
		},
		assignmentChange(position, action, matchData, user) {
			user ??= EGS.UserInformation.userDTO();

			let statusKey;

			switch (position) {
				case 'center':
					statusKey = 'crStatus';
					break;
				case 'ar1':
					statusKey = 'ar1Status';
					break;
				case 'ar2':
					statusKey = 'ar2Status';
					break;
				case 'fourth':
					statusKey = 'fourthStatus';
					break;
				case 'mentor':
					statusKey = 'mentorStatus';
					break;
				default:
					logger.error('Invalid assignment position', { position });
					return;
			}

			if (action === 'ASSIGN') {
				matchData[position] = user;
				matchData[statusKey] = 'UNCONFIRMED';
			} else if (action === 'ACCEPTED') {
				matchData[position] = user;
				matchData[statusKey] = 'ACCEPTED';
			} else if (action === 'REJECTED') {
				matchData[position] = user;
				matchData[statusKey] = 'REJECTED';
			} else if (action === 'TURNED BACK') {
				matchData[position] = user;
				matchData[statusKey] = 'TURNED_BACK';
			} else {
				matchData[statusKey] = action;
			}
		},
		uploadHeaders() {
			return {
				Date: 'matchDate',
				Time: 'matchTime',
				Home: 'homeTeam',
				Away: 'awayTeam',
				Level: 'level',
				Client: 'client',
				Group: 'group',
				Authority: 'authority',
				Rank: 'matchRank',
				League: 'league',
				Division: 'division',
				Season: 'season',
				Status: 'status',
				Notes: 'notes',
				'Assignor Notes': 'assignorNotes',
				Field: 'field',
				Venue: 'venue',
				Center: 'center',
				'Center Status': 'crStatus',
				'Center Method': 'crMethod',
				AR1: 'ar1',
				'AR1 Status': 'ar1Status',
				'AR1 Method': 'ar1Method',
				AR2: 'ar2',
				'AR2 Status': 'ar2Status',
				'AR2 Method': 'ar2Method',
				Fourth: 'fourth',
				'Fourth Status': 'fourthStatus',
				'Forth Method': 'fourthMethod',
				Mentor: 'mentor',
				'Mentor Status': 'mentorStatus',
				'Mentor Method': 'mentorMethod',
				Visibility: 'visibility',
				Gender: 'gender'
			};
		},
		exportHeaders() {
			return [
				'Match Number',
				'Group',
				'Date',
				'Time',
				'Venue',
				'Field',
				'Home Team',
				'Away Team',
				'Gender',
				'Level',
				'Rank',
				'Client',
				'Authority',
				'League',
				'Division',
				'Season',
				'Visibility',
				'Accept Reports',
				'Report Type',
				'Status',
				'CR',
				'AR 1',
				'AR 2',
				'Fourth',
				'Mentor',
				'Notes',
				'Assignor Notes'
			];
		},
		exportMatches(matches) {
			const formatOfficial = (official: UserSummary | null | undefined) => (official ? `${official.firstName ?? ''} ${official.lastName ?? ''}`.trim() : '');
			const exportValueByHeader: Record<string, (match: MatchRecord) => unknown> = {
				'Match Number': (match) => match.groupMatchNumber,
				Group: (match) => (match['group'] as DynamicRecord | null | undefined)?.['name'] ?? '',
				Date: (match) => match.matchDate,
				Time: (match) => match.matchTime,
				Venue: (match) => match.location?.name ?? '',
				Field: (match) => match.fieldEntity?.['fieldName'] ?? match.field ?? '',
				'Home Team': (match) => match['homeTeam'],
				'Away Team': (match) => match['awayTeam'],
				Gender: (match) => match['gender'],
				Level: (match) => match['level'],
				Rank: (match) => match.matchRank,
				Client: (match) => match['client']?.['shortName'] ?? '',
				Authority: (match) => match['authority'],
				League: (match) => match['league'],
				Division: (match) => match['division'],
				Season: (match) => match['season'],
				Visibility: (match) => match.visibility,
				'Accept Reports': (match) => match['acceptReport'],
				'Report Type': (match) => match['reportType'],
				Status: (match) => match.status,
				CR: (match) => formatOfficial(match.center),
				'AR 1': (match) => formatOfficial(match.ar1),
				'AR 2': (match) => formatOfficial(match.ar2),
				Fourth: (match) => formatOfficial(match.fourth),
				Mentor: (match) => formatOfficial(match.mentor),
				Notes: (match) => match['notes'],
				'Assignor Notes': (match) => match['assignorNotes']
			};
			const headers = this.exportHeaders();
			const rows = matches.map((match) => headers.map((header) => exportValueByHeader[header]?.(match) ?? ''));

			const firstMatch = matches[0];
			const lastMatch = matches[matches.length - 1];

			if (!firstMatch || !lastMatch) {
				return;
			}

			const start = firstMatch.matchDate ?? '';
			const end = lastMatch.matchDate ?? '';

			const csvContent = [headers, ...rows].map((row) => row.map(escapeCsvField).join(',')).join('\n');

			const blob = new window.Blob([csvContent], {
				type: 'text/csv;charset=utf-8;'
			});

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.setAttribute('href', url);
			link.setAttribute('download', `exportedMatches(${start} - ${end}).csv`);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
		convertUploadData(header) {
			const normalizedHeader = header.trim();
			const headersMap = this.uploadHeaders();

			return headersMap[normalizedHeader] ?? normalizedHeader;
		},
		formatUser(user) {
			const firstName = user.firstName ?? '';
			const middleName = user.middleName ?? '';
			const lastName = user.lastName ?? '';

			return {
				userNumber: user.userNumber ?? undefined,
				fullName: middleName ? `${firstName} ${middleName} ${lastName}` : `${firstName} ${lastName}`,
				firstLast: `${firstName} ${lastName}`
			};
		},
		formatUserWithNumber(user) {
			if (!user) {
				return 'Unknown User';
			}

			const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

			return `${name} (#${user.userNumber?.toString() ?? ''})`;
		},
		async initializeUser(userInformation) {
			const parsedUserInformation = JSON.parse(userInformation) as UserRecord | null;

			if (!parsedUserInformation) {
				logger.error('User information is missing');
				return '/login';
			}

			const userId = parsedUserInformation.userNumber;

			if (typeof userId !== 'number') {
				logger.error('User information is missing a valid user number');
				return '/login';
			}

			void stores.userSettingsStore.fetchUserSettings(userId).catch((error: unknown) => {
				logger.warn('Failed to prefetch user settings', { error });
			});

			await stores.usersStore.usersGroups({ force: true });

			const userRolesArray = stores.usersStore.roles;
			const userRoles = new Set(userRolesArray);

			if (!stores.usersStore.roles.length) {
				logger.warn('No roles loaded after usersGroups');
			}

			if (userRoles.has('Official') && !stores.officialRankingsStore.loadedMyRankings) {
				void stores.officialRankingsStore.fetchMyRankings().catch((error: unknown) => {
					logger.warn('Failed to prefetch rankings', { error });
				});
			}

			if (userRoles.has('Referee Assignor')) {
				const fetched = await this.fetchAssignorsClients();
				const assignorClients = Array.isArray(fetched) ? fetched : [];

				localStorage.setItem('assignor_clients', JSON.stringify(assignorClients));

				const clientIds = assignorClients.map((client) => client.clientNumber).filter((clientNumber): clientNumber is number => typeof clientNumber === 'number');

				if (clientIds.length > 0) {
					await stores.matchRequestStore.fetchRequestsForClient(clientIds);
				}
			}

			const path = getDefaultRedirectPathForRoles(userRolesArray);
			stores.authStore.refreshProfileCompleteness();
			return path;
		},
		async fetchAssignorsClients() {
			return (await stores.usersStore.userAssignor()) as ClientRecord[];
		},
		async fetchMatchesForSelectedGroups(selectedGroups, user) {
			if (selectedGroups.value.length > 0) {
				const groupNumbers = selectedGroups.value.map((group) => group.groupNumber);
				const allMatches = (await stores.matchStore.fetchMatchesByGroups(groupNumbers)) ?? [];

				stores.matchStore.groupsMatches = allMatches
					.sort((a, b) => new Date(a.dateTime ?? 0).getTime() - new Date(b.dateTime ?? 0).getTime())
					.filter((row) => row.status !== 'Canceled')
					.filter((row) => row.visibility !== 'HIDDEN')
					.filter((row) => {
						const positions = [row.center, row.ar1, row.ar2, row.fourth, row.mentor];
						const statuses = [row.crStatus, row.ar1Status, row.ar2Status, row.fourthStatus, row.mentorStatus];

						const allAccepted = statuses.every((status) => status === 'ACCEPTED');

						if (allAccepted) {
							return false;
						}

						const userInvolved = positions.some((pos) => pos?.userNumber === user?.userNumber);
						return !userInvolved;
					});
			} else {
				stores.matchStore.groupsMatches = [];
				stores.matchStore.matches = [];
			}
		},
		/*officialIsRanked(position, match, currentUser, rankings) {
			if (!match || !currentUser || !Array.isArray(rankings)) {
				console.warn('[officialIsRanked] Missing required params', {
					match,
					currentUser,
					rankingsLength: rankings?.length
				});

				return false;
			}

			const matchRank = match.matchRank ?? 0;
			const clientName = match.client?.['name'];
			const usersPosition = position + 'Rank';

			if (!clientName) {
				console.warn('[officialIsRanked] Match has no client name', {
					matchNumber: match.matchNumber
				});

				return false;
			}

			const userRanking = rankings.find((r) => {
				const clientMatch = r.client?.['name'] === clientName;
				const userMatch = r.user?.userNumber === currentUser.userNumber;
				const rankValue = r[usersPosition];
				const hasRank = typeof rankValue === 'number' && rankValue >= matchRank;

				return clientMatch && userMatch && hasRank;
			});

			if (!userRanking) {
				console.debug('[officialIsRanked] No qualifying ranking found', {
					position,
					matchRank,
					clientName,
					userNumber: currentUser.userNumber,
					rankingsForClient: rankings
						.filter((r) => r.client?.['name'] === clientName)
						.map((r) => ({
							userNumber: r.user?.userNumber,
							[usersPosition]: r[usersPosition]
						}))
				});
			}

			return !!userRanking;
		},*/
		officialIsRanked(position, match, currentUser, rankings) {
			if (!match || !currentUser || !Array.isArray(rankings)) {
				return false;
			}

			const typedMatch = match as {
				matchNumber?: number;
				matchRank?: number | string;
				group?: {
					groupNumber?: number | string;
				};
				groupNumber?: number | string;
			};

			const typedUser = currentUser as {
				userNumber?: number | string;
			};

			const typedRankings = rankings as Array<{
				group?: {
					groupNumber?: number | string;
				};
				groupNumber?: number | string;
				groupId?: number | string;
				user?: {
					userNumber?: number | string;
				};
				userNumber?: number | string;
				userId?: number | string;
				[key: string]: unknown;
			}>;

			const matchRankValue = Number(typedMatch.matchRank ?? 0);
			const matchRank = Number.isFinite(matchRankValue) ? matchRankValue : 0;
			const groupNumber = Number(typedMatch.group?.groupNumber ?? typedMatch.groupNumber);
			const userNumber = Number(typedUser.userNumber);
			const usersPosition = `${position}Rank`;

			if (!Number.isFinite(groupNumber) || groupNumber <= 0 || !Number.isFinite(userNumber) || userNumber <= 0) {
				logger.warn('[officialIsRanked] Match has no group', {
					matchNumber: typedMatch.matchNumber
				});

				return false;
			}

			const userRanking = typedRankings.find((ranking) => {
				const rankingGroupNumber = Number(ranking.group?.groupNumber ?? ranking.groupNumber ?? ranking.groupId);
				const rankingUserNumber = Number(ranking.user?.userNumber ?? ranking.userNumber ?? ranking.userId);
				const rankValue = Number(ranking[usersPosition]);
				const groupMatch = rankingGroupNumber === groupNumber;
				const userMatch = rankingUserNumber === userNumber;
				const hasRank = Number.isFinite(rankValue) && rankValue >= matchRank;
				return groupMatch && userMatch && hasRank;
			});

			return !!userRanking;
		},
		isLoggedInBase() {
			const storedRoles = localStorage.getItem('user_roles');
			const userRoles = JSON.parse(storedRoles?.trim() ? storedRoles : '[]') as unknown;

			return Array.isArray(userRoles) && userRoles.length > 0;
		},
		getUserRoles() {
			const storedRoles = localStorage.getItem('user_roles');
			const roles = JSON.parse(storedRoles?.trim() ? storedRoles : '[]') as unknown;

			return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === 'string') : [];
		},
		hasAnyRole(requiredRoles) {
			const userRoles = this.getUserRoles();
			return requiredRoles.some((role) => userRoles.includes(role));
		},
		canImpersonate() {
			const impersonationRoles = ['Evergreen Sports', 'Developer', 'Referee Administrator'];
			return this.hasAnyRole(impersonationRoles);
		},
		formatLocation(obj) {
			if (!obj) {
				return '-';
			}

			if (obj.addressLine1) {
				const cityState = [obj.city, obj.state].filter(Boolean).join(', ');
				return [obj.addressLine1, cityState, obj.postalCode5].filter(Boolean).join(', ');
			}

			const parts = [obj.city, obj.state].filter(Boolean);

			return parts.length ? parts.join(', ') : '-';
		},
		formatLocationDisplay(match) {
			if (match?.location?.name) {
				return match.location.name;
			}

			if (match?.location?.addressLine1) {
				return this.formatLocation(match.location);
			}

			if (match?.field) {
				return match.field;
			}

			return '-';
		},
		findNextAvailableAssignorSlot(client) {
			for (let i = 1; i <= 5; i++) {
				const key = `assignor${i.toString()}`;

				if (!client[key]) {
					return key;
				}
			}
			return null;
		},
		groupRoles: {
			SYSTEM_ROLES: new Set(['CUSTOMER_SUPPORT', 'TESTER', 'DEVELOPER', 'EVERGREEN_SPORTS']),
			HIDDEN_BY_DEFAULT_ROLES: new Set(['CUSTOMER_SUPPORT', 'TESTER', 'DEVELOPER', 'EVERGREEN_SPORTS']),
			ENABLED_ROLE_GROUP_IDS: new Set(['core']),
			roleLabel(role) {
				return toText(role)
					.toLowerCase()
					.split('_')
					.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
					.join(' ');
			},
			normalizeRoleNames(roles) {
				const arr = Array.isArray(roles) ? roles.filter(Boolean).map((r) => toText(r).trim().toUpperCase()) : [];
				const set = new Set(arr);
				set.add('USER');
				return Array.from(set);
			},
			getRoleGroups({ includeHidden = false } = {}) {
				const all = [
					{
						id: 'core',
						label: 'Core',
						roles: ['USER', 'OFFICIAL', 'REFEREE_ASSIGNOR', 'REFEREE_ADMINISTRATOR']
					},
					{
						id: 'club',
						label: 'Club',
						roles: ['CLUB_TEAM_MANAGER', 'CLUB_COACH', 'CLUB_SCHEDULER', 'CLUB_REGISTRAR', 'CLUB_DIRECTOR', 'CLUB_ADMINISTRATOR']
					},
					{
						id: 'league',
						label: 'League',
						roles: ['LEAGUE_STAFF', 'LEAGUE_DISCIPLINE', 'LEAGUE_SCHEDULER', 'LEAGUE_ADMINISTRATOR']
					},
					{
						id: 'participants',
						label: 'Participants',
						roles: ['PARENT', 'PLAYER']
					}
				];

				const enabled = this.ENABLED_ROLE_GROUP_IDS;
				const groups = all.filter((g) => enabled.has(g.id));
				if (includeHidden) {
					groups.push({
						id: 'system',
						label: 'System',
						roles: Array.from(this.SYSTEM_ROLES)
					});
				}
				return groups;
			},
			getAssignableRoleSet() {
				const set = new Set<string>();

				for (const g of this.getRoleGroups({ includeHidden: false })) {
					for (const r of g.roles) {
						set.add(r);
					}
				}
				set.add('USER');
				return set;
			}
		},
		cloneValue<T>(value: T): T {
			if (value instanceof Date) {
				return new Date(value.getTime()) as T;
			}

			if (Array.isArray(value)) {
				return value.map((item: unknown) => this.cloneValue(item)) as unknown as T;
			}

			if (value && typeof value === 'object') {
				return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, this.cloneValue(val)])) as T;
			}

			return value;
		},
		isPlainObject(value): value is DynamicRecord {
			return value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
		},
		buildMergePatch(original, current, normalizeValue = (value) => value) {
			const patch: DynamicRecord = {};
			const keys = new Set([...Object.keys(original ?? {}), ...Object.keys(current ?? {})]);

			for (const key of keys) {
				const originalValue = original?.[key];
				const currentValue = normalizeValue(current?.[key], key);
				const originalNormalized = normalizeValue(originalValue, key);
				if (this.isPlainObject(originalValue) && this.isPlainObject(currentValue)) {
					const nestedPatch = this.buildMergePatch(originalValue, currentValue, normalizeValue);
					if (Object.keys(nestedPatch).length > 0) {
						patch[key] = nestedPatch;
					}
					continue;
				}
				const bothDates = originalNormalized instanceof Date && currentValue instanceof Date;
				if (bothDates) {
					if (originalNormalized.getTime() !== currentValue.getTime()) {
						patch[key] = currentValue;
					}
					continue;
				}
				if (originalNormalized !== currentValue) {
					patch[key] = currentValue;
				}
			}
			return patch;
		},
		normalizeValue(value) {
			if (value === '') {
				return null;
			}

			return value;
		}
	},
	UserInformation: {
		fullName() {
			const user = getUser();

			if (!user) {
				return '';
			}

			const firstName = user.firstName ?? '';
			const middleName = user.middleName ?? '';
			const lastName = user.lastName ?? '';

			return middleName ? `${firstName} ${middleName} ${lastName}`.trim() : `${firstName} ${lastName}`.trim();
		},
		name() {
			const user = getUser();

			if (!user) {
				return '';
			}

			return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
		},
		basicInfo() {
			const user = getUser();
			if (!user) {
				return {};
			}
			return {
				userNumber: user.userNumber ?? undefined,
				firstName: user.firstName,
				commonFirstName: user.commonFirstName,
				middleName: user.middleName,
				lastName: user.lastName,
				birthdate: user.birthdate,
				userGender: user.userGender,
				parentalFirstName: user.parentalFirstName,
				parentalLastName: user.parentalLastName,
				parentalPhone: user.parentalPhone,
				parentalEmail: user.parentalEmail
			};
		},
		setBasicInfo(data) {
			const user = getUser();

			if (!user || !data) {
				return;
			}

			user.firstName = data.firstName;
			user.commonFirstName = data.commonFirstName;
			user.middleName = data.middleName;
			user.lastName = data.lastName;
			user.birthdate = data.birthdate;
			user.userGender = data.userGender;
			user.parentalFirstName = data.parentalFirstName;
			user.parentalLastName = data.parentalLastName;
			user.parentalPhone = data.parentalPhone;
			user.parentalEmail = data.parentalEmail;
			this.syncSessionUserInfo(user);
		},
		contactInfo() {
			const user = getUser();
			if (!user) {
				return {};
			}
			return {
				userNumber: user.userNumber ?? undefined,
				primaryEmail: user.primaryEmail,
				secondaryEmail: user.secondaryEmail,
				primaryPhone: user.primaryPhone,
				secondaryPhone: user.secondaryPhone,
				emergencyFirstName: user.emergencyFirstName,
				emergencyLastName: user.emergencyLastName,
				emergencyRelation: user.emergencyRelation,
				emergencyPhone: user.emergencyPhone
			};
		},
		setContactInfo(data) {
			const user = getUser();

			if (!user || !data) {
				return;
			}

			user.primaryEmail = data.primaryEmail;
			user.secondaryEmail = data.secondaryEmail;
			user.primaryPhone = data.primaryPhone;
			user.secondaryPhone = data.secondaryPhone;
			user.emergencyFirstName = data.emergencyFirstName;
			user.emergencyLastName = data.emergencyLastName;
			user.emergencyRelation = data.emergencyRelation;
			user.emergencyPhone = data.emergencyPhone;
			this.syncSessionUserInfo(user);
		},
		mailingInfo() {
			const user = getUser();
			if (!user) {
				return {};
			}
			return {
				userNumber: user.userNumber ?? undefined,
				addressLine1: user.addressLine1 ?? '',
				addressLine2: user.addressLine2 ?? null,
				city: user.city ?? '',
				state: user.state ?? '',
				postalCode5: user.postalCode5 ?? '',
				postalCode4: user.postalCode4 ?? '',
				country: user.country ?? ''
			};
		},
		setMailingInfo(data) {
			const user = getUser();

			if (!user || !data) {
				return;
			}

			user.addressLine1 = data.addressLine1 ?? '';
			user.addressLine2 = data.addressLine2 ?? null;
			user.city = data.city ?? '';
			user.state = data.state ?? '';
			user.postalCode5 = data.postalCode5 ?? '';
			user.postalCode4 = data.postalCode4 ?? '';
			user.country = data.country ?? '';

			this.syncSessionUserInfo(user);
		},
		userDTO() {
			const user = getUser();

			if (!user) {
				return {};
			}

			return {
				userNumber: user.userNumber,
				firstName: user.firstName,
				middleName: user.middleName,
				lastName: user.lastName
			};
		},
		convertToDTO(user) {
			return {
				userNumber: user.userNumber,
				firstName: user.firstName,
				middleName: user.middleName,
				lastName: user.lastName
			};
		},
		userGroups() {
			return getUser()?.groups;
		},
		setUserGroups(groups) {
			const user = getUser();

			if (!user) {
				return;
			}

			user.groups = groups;

			sessionStorage.setItem('user_info', JSON.stringify(user));
		},
		fullUser() {
			const user = getUser();

			if (!user) {
				return {};
			}

			return {
				userNumber: user.userNumber,
				firstName: user.firstName,
				middleName: user.middleName,
				lastName: user.lastName,
				birthday: user.birthdate,
				gender: user.userGender,
				primaryEmail: user.primaryEmail,
				secondaryEmail: user.secondaryEmail,
				primaryPhone: user.primaryPhone,
				secondaryPhone: user.secondaryPhone,
				street: user['street'],
				city: user.city,
				state: user.state,
				zipcode: user['zipcode'],
				country: user.country
			};
		},
		readSessionUserInfo() {
			try {
				const storedUser = sessionStorage.getItem('user_info');

				return JSON.parse(storedUser?.trim() ? storedUser : 'null') as UserRecord | null;
			} catch {
				return null;
			}
		},
		syncSessionUserInfo(nextUser) {
			if (!nextUser || typeof nextUser !== 'object') {
				return;
			}

			const sessionUser = this.readSessionUserInfo();

			if (!sessionUser?.userNumber || sessionUser.userNumber !== nextUser.userNumber) {
				return;
			}
			const sessionSafeUser = { ...nextUser };
			delete sessionSafeUser.photo;
			sessionStorage.setItem('user_info', JSON.stringify({ ...sessionUser, ...sessionSafeUser }));
			emitAuthSessionUpdated('local');
			broadcastSessionUpdated();
		},
		isBasicProfileComplete(data) {
			const basicFields = ['firstName', 'lastName', 'birthdate', 'userGender', 'primaryEmail', 'primaryPhone'];
			return basicFields.every((field) => hasProfileValue(data[field])) && !Object.values({ ...emergencyContactErrors(data), ...parentalContactErrors(data), ...mailingAddressErrors(data) }).some(Boolean);
		},
		isProfileComplete(data) {
			return this.isBasicProfileComplete(data);
		},
		financialProfileRoleNames() {
			return ['Official', 'Referee Administrator', 'Referee Assignor'];
		},
		isOverallProfileComplete(userData, financialContext = null) {
			if (!this.isBasicProfileComplete(userData)) {
				return false;
			}
			if (!financialContext) {
				return true;
			}
			const roles = Array.isArray(financialContext.userRoles) ? financialContext.userRoles : [];
			const needsPayoutSetup = roles.some((role) => this.financialProfileRoleNames().includes(role));
			return !needsPayoutSetup || financialContext.payoutSetupComplete !== false;
		}
	},
	MatchDTO: {
		fullMatch(match) {
			return {
				ar1: match.ar1,
				ar1Method: match['ar1Method'],
				ar1Status: match.ar1Status,
				ar2: match.ar2,
				ar2Method: match['ar2Method'],
				ar2Status: match.ar2Status,
				assignorNotes: match['assignorNotes'],
				authority: match['authority'],
				awayTeam: match['awayTeam'],
				center: match.center,
				client: match.client,
				crMethod: match['crMethod'],
				crStatus: match.crStatus,
				createdBy: match['createdBy'],
				createdOn: match['createdOn'],
				division: match['division'],
				field: match.field,
				location: match.location ?? null,
				fieldId: match.fieldEntity?.['fieldId'] ?? null,
				fourth: match.fourth,
				fourthMethod: match['fourthMethod'],
				fourthStatus: match.fourthStatus,
				gender: match['gender'],
				group: match['group'],
				groupMatchNumber: match.groupMatchNumber,
				homeTeam: match['homeTeam'],
				league: match['league'],
				level: match['level'],
				matchDate: match.matchDate,
				matchNumber: match.matchNumber,
				matchRank: match.matchRank,
				matchTime: match.matchTime,
				mentor: match.mentor,
				mentorMethod: match['mentorMethod'],
				mentorStatus: match.mentorStatus,
				notes: match['notes'],
				season: match['season'],
				status: match.status,
				updatedBy: match['updatedBy'],
				updatedOn: match['updatedOn'],
				visibility: match.visibility,
				acceptReport: match['acceptReport'],
				reportType: match['reportType']
			};
		}
	},
	Enums: {
		assignmentMethods: ['None', 'Manual', 'Request', 'Self Assign'],
		positionStatus: ['None', 'Open', 'Pending Assignment', 'Unconfirmed', 'Turned Back', 'Rejected', 'Accepted'],
		assignFilters: ['All', 'Self', 'Manual'],
		matchStatus: ['Normal', 'Canceled'],
		matchVisibility: ['Hidden', 'Public'],
		matchGenders: ['None', 'Male', 'Female', 'Boys', 'Girls', 'Coed'],
		gender: ['Female', 'Male', 'Other'],
		matchReportStatus: ['None', 'Pending', 'Review', 'Billed', 'Paid'],
		ledgerEntryType: ['None', 'Official Fee', 'Travel Fee', 'Assignor Fee', 'Group Fee', 'Official Payment', 'Assignor Payment', 'Client Charge', 'Client Payment', 'Fine', 'Debit Adjustment', 'Credit Adjustment', 'Refund', 'Other'],
		creditTypes: ['Official Payment', 'Assignor Payment', 'Credit Adjustment', 'Refund'],
		debitTypes: ['Official Fee', 'Travel Fee', 'Assignor Fee', 'Debit Adjustment'],
		warningTypes: ['Fine'],
		manualEntryTypes: ['Fine', 'Debit Adjustment', 'Credit Adjustment', 'Refund'],
		paymentMethods: ['Check', 'ACH/Direct Deposit', 'Cash', 'Venmo', 'PayPal', 'Zelle', 'Other'],
		durationTypes: ['Regular Season', 'Friendly', 'Tournament', 'Playoff', 'Championship'],
		fieldTypeOptions: ['Grass', 'Turf', 'Stadium', 'Indoor'],
		waypointTypes: ['Parking', 'Check-In', 'Referee Tent', 'Entrance', 'Restroom', 'Other'],
		groupPlan: ['Basic', 'Small', 'Medium', 'Large', 'Enterprise', 'Suspended', 'Custom'],
		groupJoinStatus: ['Open', 'Invite', 'Request', 'Closed'],
		groupStatus: ['Active', 'Delinquent', 'Suspended', 'Closed'],
		groupType: ['Global', 'Referee', 'Club', 'League'],
		groupSport: ['Soccer'],
		groupTravelEnforcement: ['Off', 'Warn Only', 'Strict'],
		reportType: ['Basic'],
		yesNoOptions: ['Yes', 'No'],
		datePresets: [
			{ label: 'Next 7 days', value: '7d' },
			{ label: 'Next 2 weeks', value: 'next2w' },
			{ label: 'Next 4 weeks', value: 'next4w' },
			{ label: 'All upcoming', value: 'all' },
			{ label: 'Custom', value: 'custom' }
		],
		availabilityFilters: [
			{ label: 'All', value: 'all' },
			{ label: 'Available or needs setup', value: 'available_or_missing' },
			{ label: 'Available only', value: 'available' },
			{ label: 'Needs availability', value: 'missing' },
			{ label: 'Blocked', value: 'blocked' }
		],
		requestFilters: ['All', 'Requested by me', 'Not requested by me'],
		SignupStatus: {
			EMAIL_VERIFICATION: 'EMAIL_VERIFICATION',
			QUESTIONNAIRE: 'QUESTIONNAIRE',
			AWAITING_PAYMENT: 'AWAITING_PAYMENT',
			PAYMENT_CONFIRMED: 'PAYMENT_CONFIRMED',
			ONBOARDING: 'ONBOARDING',
			COMPLETE: 'COMPLETE',
			EXPIRED: 'EXPIRED'
		},
		emailStatusOptions: ['Pending', 'Sent to Provider', 'Processed', 'Delivered', 'Opened', 'Clicked', 'Soft Bounced', 'Hard Bounced', 'Spam Complaint', 'Failed'],
		emailTypeOptions: [
			'Match Assignment',
			'Match Updated',
			'Broadcast',
			'Registration Confirmation',
			'Registration Confirmation Resend',
			'Group Invitation',
			'Password Reset',
			'Client Access Invitation',
			'Confirmation Token',
			'Support Ticket Created',
			'Support Ticket Updated',
			'Support Ticket Escalated',
			'Support Ticket Comment',
			'Payout Setup Request',
			'Payout Ready',
			'Payout Status',
			'Client Invoice',
			'Unknown'
		],
		groupPlanEntitlements: {
			Basic: {
				maxMembers: 100,
				maxRefereeAssignors: 1,
				maxRefereeAdmins: 1
			},
			Small: {
				maxMembers: 250,
				maxRefereeAssignors: 2,
				maxRefereeAdmins: 2
			},
			Medium: {
				maxMembers: 500,
				maxRefereeAssignors: 4,
				maxRefereeAdmins: 1
			},
			Large: {
				maxMembers: 750,
				maxRefereeAssignors: 4,
				maxRefereeAdmins: 2
			},
			Enterprise: {
				maxMembers: 1000,
				maxRefereeAssignors: 6,
				maxRefereeAdmins: 5
			},
			Suspended: {
				maxMembers: 0,
				maxRefereeAssignors: 0,
				maxRefereeAdmins: 0
			},
			Custom: {
				maxMembers: 0,
				maxRefereeAssignors: 0,
				maxRefereeAdmins: 0
			}
		},
		LICENSE_LABELS: {
			safety: {
				1: 'Safesport',
				2: 'Backgroud Check: Clear',
				3: 'Safe Soccer Introduction',
				4: 'Safe Soccer Phase 1',
				5: 'Safe Soccer Phase 2',
				6: 'Safe Soccer Cleared'
			},
			referee: {
				1: 'FIFA Referee',
				2: 'FIFA Assistant Referee',
				3: 'PRO Referee',
				4: 'PRO Assistant Referee',
				5: 'National Referee',
				6: 'National Assistant Referee',
				7: 'Regional Referee',
				8: 'Referee',
				9: 'National Referee Coach',
				10: 'Referee Coach',
				11: 'FIFA Futsal Referee',
				12: 'Regional Futsal Referee',
				13: 'Beach Soccer Referee',
				14: 'Indoor Soccer Referee',
				15: 'National Emeritus Referee',
				16: 'Regional Emeritus Referee',
				17: 'National Assignor',
				18: 'Assignor',
				19: 'Regional Assessor',
				20: 'Grassroots Assessor',
				21: 'Regional Instructor',
				22: 'Grassroots Instructor',
				29: 'Grassroots Futsal Referee',
				31: 'National Assessor',
				32: 'National Instructor',
				37: 'Referee Mentor',
				38: 'VAR/AVAR',
				39: 'FIFA VMO',
				40: 'PRO 2 Referee',
				41: 'PRO 2 Assistant Referee',
				42: 'VMO',
				43: 'PRO 2 VMO',
				44: 'Armed Forces Referee Achievement',
				45: 'Digital Referee',
				46: 'PRO Assignor',
				47: 'Referee Mentor +'
			},
			coach: {
				1: 'National F',
				2: 'National E',
				3: 'National D',
				4: 'National C',
				5: 'National B',
				6: 'National A',
				7: 'Academy Director',
				9: 'National Goalkeeping',
				10: 'National Youth License',
				14: 'A - Youth',
				15: 'A - Senior',
				16: 'Pro License',
				17: 'Grassroots Instructor Level 1',
				18: '4v4 License (online)',
				19: '7v7 License (online)',
				20: '9v9 License (online)',
				21: '11v11 License (online)',
				22: '4v4 License (in-person)',
				23: '7v7 License (in-person)',
				24: '9v9 License (in-person)',
				25: '11v11 License (in-person)',
				26: 'Goalkeeper A',
				27: 'Talent Scout',
				28: 'Director of Coaching',
				29: 'Grassroot Instructor Level 2',
				30: 'Coach Educator C',
				31: 'Coach Educator B',
				32: 'Coach Educator A',
				33: 'Coach Educator Grassroots',
				34: 'Coach Educator D',
				35: 'Educator-Club Technical Leadership Level 1',
				36: 'Talent Scout Level 2',
				37: 'Club Technical Leadership Level 1',
				38: 'Club Technical Leadership Level 2',
				39: 'Club Technical Leadership Level 3',
				40: 'Coach Educator Goalkeeper C',
				41: 'Coach Educator Pro',
				42: 'Goalkeeper C',
				43: 'Goalkeeper B',
				44: 'Educator Developer'
			}
		}
	},
	Validation: {
		isRequired(value) {
			if (value === null || value === undefined) {
				return false;
			}

			if (typeof value === 'string') {
				return value.trim().length > 0;
			}

			if (typeof value === 'number') {
				return !Number.isNaN(value);
			}

			if (Array.isArray(value)) {
				return value.length > 0;
			}

			return Boolean(value);
		},
		isEmail(value) {
			if (!value) {
				return false;
			}

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

			return emailRegex.test(toText(value).trim());
		},
		isPhone(value) {
			if (!value) {
				return true;
			}

			const cleaned = toText(value).replace(/\D/g, '');

			return cleaned.length === 10 || cleaned.length === 11;
		},
		isPositiveNumber(value) {
			if (value === null || value === undefined || value === '') {
				return false;
			}

			const num = Number(value);

			return !Number.isNaN(num) && num > 0;
		},
		isNonNegativeNumber(value) {
			if (value === null || value === undefined || value === '') {
				return false;
			}

			const num = Number(value);

			return !Number.isNaN(num) && num >= 0;
		},
		isDate(value) {
			if (!value) {
				return false;
			}

			if (value instanceof Date) {
				return !Number.isNaN(value.getTime());
			}

			if (typeof value !== 'string' && typeof value !== 'number') {
				return false;
			}

			const date = new Date(value);

			return !Number.isNaN(date.getTime());
		},
		isPayPeriod(value) {
			if (!value) {
				return false;
			}

			return /^\d{4}-\d{2}$/.test(toText(value).trim());
		},
		validateFields(data, requiredFields) {
			const errors: Record<string, string> = {};

			for (const field of requiredFields) {
				if (!this.isRequired(data?.[field])) {
					errors[field] = `${field} is required`;
				}
			}
			return {
				isValid: Object.keys(errors).length === 0,
				errors
			};
		},
		invalidClass(condition) {
			return condition ? 'egs-invalid' : '';
		},
		isUsAbaRoutingNumber(value) {
			if (value === null || value === undefined) {
				return false;
			}

			const d = toText(value).replace(/\D/g, '');

			if (d.length !== 9) {
				return false;
			}

			const digits = d.split('').map(Number);

			if (digits.some((n) => Number.isNaN(n))) {
				return false;
			}

			const [digit0 = 0, digit1 = 0, digit2 = 0, digit3 = 0, digit4 = 0, digit5 = 0, digit6 = 0, digit7 = 0, digit8 = 0] = digits;
			const sum = 3 * (digit0 + digit3 + digit6) + 7 * (digit1 + digit4 + digit7) + (digit2 + digit5 + digit8);

			return sum % 10 === 0;
		},
		isUsBankAccountNumber(value) {
			if (value === null || value === undefined) {
				return false;
			}

			const d = toText(value).replace(/\D/g, '');

			return d.length >= 4 && d.length <= 17;
		}
	}
};

export default EGS;
